/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";

import { useNavigate } from "react-router";

import Box from "@mui/material/Box";

import LogClient from "@mnemonic/logging";
import { RemoveSplash } from "@mnemonic/mui";

export const DEFAULT_ERROR_KEY = "__default__";

export interface IErrorBoundaryProps extends React.PropsWithChildren {
  handlers: { [key: string]: { Component: React.ComponentType<any>; onError?: () => void } };
}

interface IErrorBoundaryState {
  error: null | Error;
  info: any;
  Component: null | React.ComponentType<any>;
}

export class ErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
  constructor(props) {
    super(props);
    this.state = {
      Component: null,
      error: null,
      info: null,
    };
  }

  public componentDidCatch(error, info) {
    LogClient.getInstance()?.logException(error, {
      componentStack: info.componentStack,
    });

    // Check if we should handle it
    let handler;
    Object.keys(this.props.handlers).forEach((k) => {
      if (handler) return;
      if (error.name === k) {
        handler = this.props.handlers[k];
      }
    });

    // If a default handler is sat use that
    if (!handler && Object.prototype.hasOwnProperty.call(this.props.handlers, DEFAULT_ERROR_KEY)) {
      handler = this.props.handlers[DEFAULT_ERROR_KEY];
    }

    // Not handle
    if (!handler) throw error;

    // Handle it
    const { Component, onError } = handler;
    if (onError) {
      onError.call(this, error, info);
    }
    this.setState({ error, info, Component });
  }

  public render() {
    const { error, info, Component } = this.state;
    const { children, ...restProps } = this.props;
    if (error && Component) {
      return (
        <Box sx={{ marginTop: 8 }}>
          <RemoveSplash />
          <Component {...restProps} {...{ error, info }} />
        </Box>
      );
    }
    return this.props.children;
  }
}

export const RouterAwareErrorBoundary = withHistoryHook(ErrorBoundary);

function withHistoryHook(Component) {
  return function WrappedComponent(props) {
    const navigate = useNavigate();
    return <Component {...props} history={navigate} />;
  };
}
